import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { soloApi } from "@/lib/api";
import { AGENTS } from "@/lib/agents";
import { useAuth } from "@/context/AuthContext";
import CEOChat from "@/components/CEOChat";
import { Inbox, DollarSign, Users, Sparkles, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const stageLabel = (s) => ({ 0: "Co-founder mode", 1: "Launch mode", 2: "Operator mode" }[s] || "—");

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    soloApi.standup().then(({ data }) => setData(data));
  }, []);

  return (
    <div className="p-8 pb-16">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <div className="font-mono text-xs uppercase tracking-widest text-neon-cyan mb-2">✦ Daily standup · {stageLabel(user?.stage)}</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold"><span className="chrome-text">Morning, {user?.name?.split(" ")[0]}.</span></h1>
        <p className="text-white/70 mt-2">Here's what your crew did and what needs your yes.</p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* stats row */}
        <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard tid="stat-approvals" icon={Inbox} color="pink" label="Waiting on you" value={data?.pending_approvals?.length ?? 0} sub="approvals" />
          <StatCard tid="stat-money" icon={DollarSign} color="lime" label="Money at stake" value={`$${(data?.money_at_stake ?? 0).toFixed(0)}`} sub="in pending actions" />
          <StatCard tid="stat-leads" icon={Users} color="cyan" label="Leads" value={data?.leads_count ?? 0} sub="in pipeline" />
        </div>

        <div className="lg:col-span-4 lg:row-span-3">
          <CEOChat compact />
        </div>

        {/* what happened */}
        <div className="lg:col-span-8 glass rounded-3xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl font-bold flex items-center gap-2"><Sparkles className="text-neon-lime" /> Yesterday your crew</h2>
            <Link to="/approvals" className="text-neon-cyan text-sm font-semibold flex items-center gap-1 hover:underline" data-testid="dash-view-approvals">Approvals <ArrowRight className="w-4 h-4" /></Link>
          </div>
          {(data?.yesterday?.length ?? 0) === 0 ? (
            <p className="text-white/60">Nothing to report yet. Chat with Casey or ask an agent to draft something.</p>
          ) : (
            <div className="space-y-3">
              {data.yesterday.slice(0, 6).map((t) => {
                const a = AGENTS[t.agent] ?? AGENTS.ceo;
                return (
                  <motion.div
                    key={t.task_id}
                    initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                    className="flex items-start gap-3 p-3 rounded-2xl bg-white/5 border border-white/10"
                    data-testid={`dash-task-${t.task_id}`}
                  >
                    <div className="w-9 h-9 slime-blob overflow-hidden flex-shrink-0" style={{ background: a.gradient }}>
                      <img src={a.avatar} alt="" className="w-full h-full object-cover mix-blend-luminosity" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-[10px] uppercase tracking-widest" style={{ color: a.color }}>{a.name} · {a.role}</div>
                      <div className="font-semibold">{t.title}</div>
                      <div className="text-sm text-white/70 truncate">{t.summary}</div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>

        {/* pending approvals */}
        <div className="lg:col-span-8 glass rounded-3xl p-6">
          <h2 className="font-display text-2xl font-bold mb-4 flex items-center gap-2"><Inbox className="text-neon-pink" /> Needs your call</h2>
          {(data?.pending_approvals?.length ?? 0) === 0 ? (
            <p className="text-white/60">Inbox zero. Enjoy the vibes.</p>
          ) : (
            <div className="space-y-3">
              {data.pending_approvals.slice(0, 4).map((t) => (
                <Link
                  key={t.task_id}
                  to="/approvals"
                  data-testid={`dash-pending-${t.task_id}`}
                  className="flex items-start gap-3 p-3 rounded-2xl bg-neon-pink/10 border border-neon-pink/30 hover:bg-neon-pink/15 transition-colors"
                >
                  <div className="font-semibold flex-1">{t.title} <span className="text-white/60 text-sm block">{t.summary}</span></div>
                  <ArrowRight className="w-4 h-4 text-neon-pink" />
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, color, label, value, sub, tid }) {
  const glow = { pink: "neon-glow-pink", lime: "neon-glow-lime", cyan: "neon-glow-cyan" }[color];
  const txt = { pink: "text-neon-pink", lime: "text-neon-lime", cyan: "text-neon-cyan" }[color];
  return (
    <motion.div whileHover={{ y: -4 }} className={"glass rounded-3xl p-5 " + glow} data-testid={tid}>
      <Icon className={"w-6 h-6 mb-3 " + txt} />
      <div className="font-mono text-[10px] uppercase tracking-widest text-white/60">{label}</div>
      <div className="font-display text-4xl font-bold mt-1">{value}</div>
      <div className="text-xs text-white/60">{sub}</div>
    </motion.div>
  );
}
